import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Loader2,
  Package,
  RefreshCw,
  Search,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { PageContainer, PageHeader, Card, Pill } from "@/components/layout/PageShell";
import {
  OnlineOrderDetailSheet,
  STATUS_TONES,
  formatStatus,
  sourceBadge,
} from "@/components/online/OnlineOrderDetailSheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { cn } from "@/lib/utils";

const STATUS_FILTERS = [
  { id: "", label: "All completed" },
  { id: "delivered", label: "Delivered" },
  { id: "picked_up", label: "Picked up" },
  { id: "cancelled", label: "Cancelled" },
];

const SOURCE_FILTERS = [
  { id: "", labelKey: "onlineOrders.sourceAll" },
  { id: "storefront", labelKey: "onlineOrders.sourceOnline" },
  { id: "call", labelKey: "onlineOrders.sourceCall" },
];

function SortIcon({ column, sort, direction }) {
  if (sort !== column) return <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />;
  return direction === "asc" ? (
    <ArrowUp className="h-3.5 w-3.5" />
  ) : (
    <ArrowDown className="h-3.5 w-3.5" />
  );
}

function useDebounced(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function OnlineOrderHistory() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const branchId = user?.default_branch?.id;
  const currency = user?.company_currency || user?.company?.currency || user?.default_branch?.currency || "AED";

  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sort, setSort] = useState("placed_at");
  const [direction, setDirection] = useState("desc");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, sourceFilter, debouncedSearch, fromDate, toDate, sort, direction]);

  const { data: listData, isLoading, isFetching, refetch } = useQuery({
    queryKey: [
      "online-orders",
      branchId,
      "history",
      statusFilter,
      sourceFilter,
      debouncedSearch,
      fromDate,
      toDate,
      sort,
      direction,
      page,
    ],
    queryFn: async () => {
      const { data: res } = await api.get("/online-orders", {
        params: {
          branch_id: branchId,
          scope: "history",
          status: statusFilter || undefined,
          source: sourceFilter || undefined,
          search: debouncedSearch || undefined,
          from: fromDate || undefined,
          to: toDate || undefined,
          sort,
          direction,
          page,
          per_page: 25,
        },
      });
      return res;
    },
    enabled: Boolean(branchId),
    placeholderData: keepPreviousData,
  });

  const orders = listData?.orders ?? [];
  const meta = listData?.meta ?? { current_page: 1, last_page: 1, total: 0 };

  const toggleSort = (column) => {
    if (sort === column) {
      setDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSort(column);
      setDirection("desc");
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title={t("onlineOrders.historyTitle")}
        subtitle={t("onlineOrders.historySubtitle")}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/online/orders">
                <Package className="h-4 w-4" />
                Active board
              </Link>
            </Button>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </Button>
          </div>
        }
      />

      <div className="mb-3 flex flex-wrap gap-2">
        {SOURCE_FILTERS.map((f) => (
          <button
            key={f.id || "all-sources"}
            type="button"
            onClick={() => setSourceFilter(f.id)}
            className={cn(
              "px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
              sourceFilter === f.id
                ? "bg-brand-brown text-white border-brand-brown"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-400",
            )}
          >
            {t(f.labelKey)}
          </button>
        ))}
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.id || "all"}
            type="button"
            onClick={() => setStatusFilter(f.id)}
            className={cn(
              "px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
              statusFilter === f.id
                ? "bg-brand-orange text-white border-brand-orange"
                : "bg-white text-slate-600 border-slate-200 hover:border-orange-300",
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">From</label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-1 h-9" />
        </div>
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">To</label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-1 h-9" />
        </div>
        <div className="relative min-w-[220px] flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("onlineOrders.searchPlaceholder")}
            className="pl-9 h-9"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Card className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-[#FFF7F0] text-left text-muted-foreground">
                {[
                  { id: "reference", label: "Reference" },
                  { id: "customer_name", label: "Customer" },
                  { id: "mode", label: "Mode" },
                  { id: "status", label: "Status" },
                  { id: "net_amount", label: "Amount" },
                  { id: "placed_at", label: "Placed" },
                ].map((c) => (
                  <th key={c.id} className={cn("p-3", c.id === "net_amount" && "text-right")}>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 font-medium hover:text-slate-800"
                      onClick={() => toggleSort(c.id)}
                    >
                      {c.label}
                      <SortIcon column={c.id} sort={sort} direction={direction} />
                    </button>
                  </th>
                ))}
                <th className="p-3">Source</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((row) => (
                <tr
                  key={row.id}
                  className="border-b cursor-pointer hover:bg-orange-50/50"
                  onClick={() => setSelectedId(row.id)}
                >
                  <td className="p-3 font-mono text-xs">{row.reference}</td>
                  <td className="p-3">
                    <div className="font-medium">{row.customer_name}</div>
                    <div className="text-xs text-slate-500">{row.customer_phone}</div>
                  </td>
                  <td className="p-3 capitalize">{row.mode}</td>
                  <td className="p-3">
                    <Pill tone={STATUS_TONES[row.status] || "default"}>{formatStatus(row.status)}</Pill>
                  </td>
                  <td className="p-3 text-right font-medium">
                    {money(row.net_amount, row.currency_code || currency)}
                  </td>
                  <td className="p-3 text-xs text-slate-500">
                    {row.placed_at ? new Date(row.placed_at).toLocaleString() : "—"}
                  </td>
                  <td className="p-3">{sourceBadge(row.source, t)}</td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-500">
                    {t("onlineOrders.emptyHistory")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="flex flex-wrap items-center justify-between gap-2 border-t px-3 py-2 text-xs text-slate-500">
            <span>
              {meta.total
                ? `Showing ${meta.from ?? 0}–${meta.to ?? 0} of ${meta.total}`
                : "No results"}
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1 || isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span>
                Page {meta.current_page || page} of {meta.last_page || 1}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= (meta.last_page || 1) || isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </Card>
      )}

      <OnlineOrderDetailSheet
        orderId={selectedId}
        branchId={branchId}
        currency={currency}
        onClose={() => setSelectedId(null)}
        readOnly
      />
    </PageContainer>
  );
}
